<template>
  <div>
    <Layout :containers="containers">
      <template v-slot:header>
        <DefaultHeroBar
          :hasQuickStartCallout="false"
          :heroTitle="'Create your will, your way, with expert support '"
          :heroDescription="`Planning for the future can be overwhelming, but we make it straightforward, secure, and stress-free.`"
          :section="'howItWorks'"
        />
      </template>
      <template v-slot:tabbed-content>
        <Breadcrumb />
        <TitleRow
          :title="'Ensure your wishes can be honoured '"
          :description="'Our expert-backed process and solutions ensure that your final wishes can be honoured, giving you and your loved ones peace of mind.'"
        />

        <TabbedContentComponent
          v-if="!isMobile"
          :tabTitles="[
            'The Process',
            'Information You Need',
            'Finalisation & Safe Storage',
            'Protect Your Estate',
          ]"
        >
          <template v-slot:tab-zero>
            <ImageTextBanner
              :imageUrl="tabOneDetails.imageUrl"
              :title="tabOneDetails.title"
              :paragraphOne="tabOneDetails.paragraphOne"
              :reversed="tabOneDetails.reversed"
              :buttonText="tabOneDetails.buttonText"
              @gradientButtonClicked="gradientButtonClicked"
            />
            <HR />
          </template>
          <template v-slot:tab-one>
            <Accordion :accordionData="accordionSubData" :html="true" />
            <HR />
          </template>
          <template v-slot:tab-two>
            <ImageTextBanner
              :imageUrl="tabThreeDetails.imageUrl"
              :title="tabThreeDetails.title"
              :paragraphOne="tabThreeDetails.paragraphOne"
              :reversed="tabThreeDetails.reversed"
              :buttonText="tabThreeDetails.buttonText"
              @gradientButtonClicked="costCalculator"
            />
            <HR />
          </template>
          <template v-slot:tab-three>
            <ImageTextBanner
              :imageUrl="tabFourDetails.imageUrl"
              :title="tabFourDetails.title"
              :paragraphOne="tabFourDetails.paragraphOne"
              :reversed="tabFourDetails.reversed"
              :buttonText="tabFourDetails.buttonText"
              @gradientButtonClicked="costCalculator"
            />
            <HR />
          </template>
        </TabbedContentComponent>
        <MobileTabbedContent
          v-else
          :accordionData="accordionData"
          :accordionSubData="accordionSubData"
          :hideButton="whitelabel.brand === 'teba'"
          @gradientButtonClicked="signup"
        />
      </template>
      <template v-slot:easy-updates>
        <div class="section-padding">
          <ImageTextBanner
            :imageUrl="easyUpdatesDetails.imageUrl"
            :title="easyUpdatesDetails.title"
            :paragraphOne="easyUpdatesDetails.paragraphOne"
            :reversed="true"
            :buttonText="easyUpdatesDetails.buttonText"
            @gradientButtonClicked="login"
            :bottomMargin="false"
          />
        </div>
      </template>
      <template v-slot:further-questions>
        <div class="section-padding">
          <FurtherQuestionsBanner />
        </div>
      </template>
      <template v-slot:articles>
        <div class="section-padding">
          <ArticleTilesContainer :tiles="articleTiles" :articles="articles" />
        </div>
      </template>
    </Layout>
    <SuccessSnackbar v-model="showAlert" :message="alertMessage" />
  </div>
</template>

<script>
  import Breadcrumb from '../../common/Breadcrumb.vue';
  import FurtherQuestionsBanner from '../../common/FurtherQuestionsBanner.vue';
  import TabbedContentComponent from '../../common/TabbedContentComponent.vue';
  import ArticleTilesContainer from '../articles/ArticleTilesContainer.vue';
  import ImageTextBanner from '../../guest/ImageTextBanner.vue';
  import Accordion from '../../common/Accordion.vue';
  import MobileTabbedContent from './MobileTabbedContent.vue';
  import HR from '../../common/HR.vue';
  import Layout from '../../common/layouts/Layout.vue';
  import DefaultHeroBar from '../../common/DefaultHeroBar.vue';
  import TitleRow from '../../common/TitleRow.vue';
  import DynamicNavDrawer from '../../guest/DynamicNavDrawer.vue';
  import DrawerSignIn from '../../guest/DrawerSignIn.vue';
  import DrawerSignUp from '../../guest/DrawerSignUp.vue';
  import SuccessSnackbar from '../../common/SuccessSnackbar.vue';

  export default {
    name: 'HowItWorks',
    components: {
      Breadcrumb,
      TabbedContentComponent,
      FurtherQuestionsBanner,
      ArticleTilesContainer,
      ImageTextBanner,
      Accordion,
      MobileTabbedContent,
      Layout,
      DefaultHeroBar,
      HR,
      TitleRow,
      DynamicNavDrawer,
      DrawerSignIn,
      DrawerSignUp,
      SuccessSnackbar,
    },
    metaInfo() {
      return {
        title: this.whitelabel.metaData.howItWorks.title,
        meta: [
          {
            name: 'description',
            content: this.whitelabel.metaData.howItWorks.description,
          },
          {
            property: 'og:title',
            content: this.whitelabel.metaData.howItWorks.ogTitle,
          },
          {
            property: 'og:image',
            content: this.whitelabel.metaData.howItWorks.ogImage,
          },
          {
            property: 'og:type',
            content: this.whitelabel.metaData.howItWorks.ogType,
          },
          {
            property: 'og:url',
            content: this.whitelabel.metaData.howItWorks.ogUrl,
          },
          {
            property: 'og:description',
            content: this.whitelabel.metaData.howItWorks.description,
          },
          {
            property: 'og:site_name',
            content: this.whitelabel.metaData.howItWorks.ogSiteName,
          },
        ],
      };
    },
    data() {
      return {
        signInDrawerOpen: false,
        signUpDrawerOpen: false,
        showAlert: false,
        alertMessage: null,
        countryPattern: require('@/assets/shared/redesign/backgrounds/country-pattern.svg'),
        greenAccentOne: require('@/assets/shared/redesign/backgrounds/green-accent-1.svg'),
        greenAccentTwo: require('@/assets/shared/redesign/backgrounds/green-accent-2.svg'),
        purpleAccent: require('@/assets/shared/redesign/backgrounds/purple-accent.svg'),
        tribalPattern: require(`@/assets/old_mutual/homepage/generic.svg`),
      };
    },
    methods: {
      successResetPassword() {
        this.signInDrawerOpen = false;
        this.alertMessage =
          this.whitelabel.authentication.resetPassword.successMessage;
        this.showAlert = true;
      },
      drawerClosed() {
        this.signInDrawerOpen = false;
      },
      signUpDrawerClosed() {
        this.signUpDrawerOpen = false;
      },
      gradientButtonClicked() {},
      signup() {
        this.$router.push('/register');
      },
      login() {
        this.$router.push('/Login');
      },
      costCalculator() {
        this.$router.push({
          name: 'EstateAdministrationCostCalculator',
        });
      },
    },
    computed: {
      isMobile() {
        return this.$isMobileDevice();
      },
      containers() {
        return [
          {
            name: 'header',
            background: 'countryPattern',
            class: 'hero-banner',
          },
          {
            name: 'tabbed-content',
            background: 'countryPattern',
            fadedRow: true,
            accents: ['green-accent-1'],
            class: 'tabbed-content',
          },
          {
            name: 'easy-updates',
            background: 'accent',
            class: 'how-it-works',
            accents: ['purple-accent-1'],
            elevated: true,
          },
          {
            name: 'further-questions',
            background: 'accent',
            accents: ['green-accent-2'],
            class: 'further-questions',
            hr: true,
          },
          { name: 'articles' },
        ];
      },
      articleTiles() {
        return this.whitelabel.articleTiles;
      },
      articles() {
        return this.whitelabel.articles;
      },
      accordionSubData() {
        return [
          {
            title: 'Personal Information',
            content:
              'Covers basic details, including name, date of birth, ID number, contact details, and marital status.',
          },
          {
            title: 'Family Details',
            content: `Share information about your spouse, children and designated guardians for minors, together with testamentary trust information.`,
          },
          {
            title: 'Asset Information',
            content: `List your assets, such as property, vehicles, investments and items of significant value.`,
          },
          {
            title: 'Beneficiaries',
            content: `Specify who will inherit assets from your estate, and how it will be distributed.`,
          },
          {
            title: 'Executor',
            content: `Old Mutual will be appointed as professional executor to carry out your last wishes and wind up your estate when you create your will online with us.`,
          },
          {
            title: 'Trusts',
            content: `Set up testamentary trusts as part of the process and nominate the trustees.`,
          },
          {
            title: 'Letter of Wishes',
            content: `This is optional and can include guidance for your executors and loved ones should you wish.`,
          },
          {
            title: 'Living Will',
            content: `Outline your medical treatment preferences in case you are unable to make decisions for yourself.`,
          },
          {
            title: 'Specific Bequests',
            content: `A bequest is a gift of money, property, or specific items that someone leaves to another person or organization in their will. It's essentially what you decide to give to someone after you pass away, such as leaving a certain amount of money to a friend or donating a piece of jewelry to a charity.`,
          },
        ];
      },
      tabOneDetails() {
        return {
          imageUrl: this.whitelabel.images.howItWorksProcess,
          title: `The Process`,
          paragraphOne: `A will is a vital document that captures your last wishes. At Old Mutual, we guide you step-by-step through the process, ensuring you answer all the right questions.
         You’ll provide personal details, family information (such as spouse, children, and guardians), asset information, and list your beneficiaries.<br/><br/>
         Additionally, you can choose to:
        <br/><br/>
        <ul>
        <li>Set up testamentary trusts</li>
        <li>Make specific bequests</li>
        <li>Generate a Living Will</li>
        <li>Record a Letter of Wishes</li>
        <li>Download your will in your <a href="/articles/your-will-in-a-language-you-call-your-own" target="_blank" rel="noopener">home language*</a>.  </li>
        </ul>
        <br />
        Old Mutual will be appointed as the professional executor of your estate, ensuring it’s settled efficiently and reducing the burden on your loved ones.
        <br /><br />
        We offer helpful information throughout the process, and expert support is available whenever you need it, via chat, phone or email.
        <br /><br />
        <p><i>*Available in English, Afrikaans, isiZulu, isiXhosa or Sepedi</i></p>
        `,
          reversed: false,
          buttonText: null,
        };
      },
      tabThreeDetails() {
        return {
          imageUrl: this.whitelabel.images.howItWorksSafeStorage,
          title: `Finalisation & Safe Storage`,
          paragraphOne: `After completing the process, download your will, print, and sign it by following the provided instructions. Then, upload the signed copy for expert verification.
        <br/>
        <br/>
        When you upload your will to us, we check to ensure it is correctly signed for legal validity and that the witnesses are legally permitted to sign.
        <br /><br />
        Once verified, we will collect your will free of charge and keep it in safe storage. 
        This ensures that your will is securely stored and easily accessible for quick and efficient estate finalisation. 
        This service reduces the administrative burden on your loved ones, helping to ensure your wishes are carried out smoothly.

        `,
          reversed: false,
          buttonText: null,
        };
      },
      tabFourDetails() {
        return {
          imageUrl: this.whitelabel.images.howItWorksProtectYourEstate,
          title: `Protect your Estate`,
          paragraphOne: `When you pass away, your loved ones will be required to settle costs related to winding up your estate. As part of our 
        process we provide you with a personalised cost calculation and the option secure your estate through our EstateAssist product. 
        EstateAssist provides cover for the cost such as executor fees, conveyancing fees, masters fees, standard administrative costs as 
        well as estate duties and taxes that may be payable. Cover for education and cash flow needs, and debt protection are available as optional benefits.`,
          reversed: false,
          buttonText: 'Calculate My Costs Now',
        };
      },

      easyUpdatesDetails() {
        return {
          imageUrl: this.whitelabel.images.howItWorksUpdateWill,
          title: `<span style="font-weight:500">Updating</span><span style="font-weight:300"> your will</span>`,
          paragraphOne: `We understand that life changes, and so do your needs. 
        That’s why we’ve made it simple to update your will whenever necessary.
        <br /><br />
        Just log in to your account, navigate to the section you want to update, and make your changes. 
        Once you’re done, follow the steps to finalise and securely store the new version, 
        ensuring your will always reflects your current wishes.
        `,
          reversed: true,
          buttonText: 'Log In',
        };
      },
      accordionData() {
        return this.whitelabel.howItWorksData;
      },
    },
    created() {},
    mounted() {},
  };
</script>
<style lang="scss" scoped>
  .main {
    background-color: #f9f9f9;
  }

  .how-it-works-title-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-left: 20px;
    padding-right: 20px;

    h1 {
      font-family: Montserrat;
      font-size: 28px;
      font-weight: 500;
      line-height: 34px;
      letter-spacing: -0.0025em;
      text-align: center;
      margin-bottom: 24px;
    }
    p {
      font-family: Montserrat;
      font-size: 22px;
      font-weight: 400;
      line-height: 26px;
      text-align: center;
      margin-bottom: 53px;
    }
  }
  .content-container {
    max-width: 1600px;
    z-index: 12;

    &.breadcrumb {
      max-width: unset !important;
      padding-top: 24px;
    }
  }

  .fluid-container {
    padding: 0;
    margin: 0;
    &.create-your-will {
      max-height: 309px !important;
      position: relative;
      background-color: white;
      z-index: 12;
      .content-row {
        margin-top: unset;
      }
    }
  }

  .fade-row {
    position: relative;
    background: linear-gradient(
      to bottom,
      rgba(249, 249, 249, 0.2) 0%,
      rgba(249, 249, 249, 0.3) 15%,
      rgba(249, 249, 249, 0.4) 30%,
      rgba(249, 249, 249, 0.4) 45%,
      rgba(249, 249, 249, 0.7) 65%,
      rgba(249, 249, 249, 0.9) 85%,
      rgba(249, 249, 249, 1) 100%
    );
    z-index: 12;
  }
  .content-row {
    padding: 88px 0;
    margin-top: auto;
    &.hr {
      padding: 0;
    }

    &.calculator-banner,
    &.further-questions {
      position: relative;
    }

    &.breadcrumb {
      padding-top: 0;
    }
  }

  .green-accent-1 {
    position: absolute;
    width: 150px;
    left: 0;
    top: 25px;
  }
  .green-accent-2 {
    position: absolute;
    top: 140px;
    width: 320px;
    left: -120px;
  }
  .purple-accent {
    position: absolute;
    width: 200px;
    right: -50px;
    bottom: 384px;
  }

  @media only screen and (min-width: 0px) and (max-width: 959px) {
    .content-container {
      margin: 0;
    }
    .content-row {
      padding: 52px 0;
    }
  }
</style>
